/**
 * Recommendations Workspace (PRD 10.2).
 *
 * Map, ranked list, bundle tray and budget meter, with map and list selection
 * kept in sync. The eligibility funnel is shown as prominently as the results,
 * because "why is this site not here?" is the question planners actually ask.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { AssetScoreOut, BriefResponse, RunResponse } from '../lib/types'
import {
  ASSET_CLASS_LABEL,
  EXCLUSION_LABEL,
  OBJECTIVE_FUNCTION_LABEL,
  dateRange,
  money,
  moneyShort,
  percent,
  titleCase,
} from '../lib/format'
import { Callout, EmptyState, ErrorCallout } from '../components/Callout'
import { AvailabilityChip, Chip, ConfidenceChip, DataClassChip } from '../components/Chips'
import { FeatureMetric, Metric, Skeleton } from '../components/Metric'
import { AssetMap, type MapAsset } from '../components/AssetMap'
import { ScoreBreakdown } from '../components/ScoreBreakdown'
import { AssetDetail } from '../components/AssetDetail'
import { AgentPanel } from '../components/AgentPanel'
import { ResizablePanel } from '../components/ResizablePanel'
import { StepIndicator } from '../components/StepIndicator'
import { applyBriefStateEvent } from '../lib/briefState'

const OBJECTIVES = ['balanced', 'max_target_impressions', 'max_eots', 'max_suitability', 'min_cpti']

//: The run is one blocking call with no incremental progress from the
//: server, so this advances on a timer rather than a real signal - just
//: enough to show the planner the pipeline is working through stages, not a
//: literal readout of what the backend is doing right now.
const RECOMMENDATION_STEPS = [
  'Checking eligible inventory',
  'Reading survey, audience and mobility data',
  'Scoring candidate assets',
  'Optimizing the bundle',
]

function RecommendationProgress() {
  const [step, setStep] = useState(0)
  useEffect(() => {
    setStep(0)
    const id = setInterval(() => {
      setStep((s) => Math.min(s + 1, RECOMMENDATION_STEPS.length - 1))
    }, 2500)
    return () => clearInterval(id)
  }, [])
  return (
    <div className="card">
      <div className="card-body stack tight">
        {RECOMMENDATION_STEPS.map((label, i) => (
          <div className="row tight" key={label}>
            <span aria-hidden style={{ width: 14, textAlign: 'center' }}>
              {i < step ? '✓' : i === step ? '…' : '○'}
            </span>
            <span className={i <= step ? '' : 'faint'}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function Recommendations() {
  const { briefId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [objective, setObjective] = useState('balanced')
  const [radius, setRadius] = useState(500)
  const [run, setRun] = useState<RunResponse | null>(null)
  const [focused, setFocused] = useState<string | null>(null)
  const [detail, setDetail] = useState<string | null>(null)
  const [locked, setLocked] = useState<string[]>([])
  const [excluded, setExcluded] = useState<string[]>([])
  const [onlyBundle, setOnlyBundle] = useState(false)
  const [selectedMediaTypes, setSelectedMediaTypes] = useState<string[]>([])
  const [selectedAssetClasses, setSelectedAssetClasses] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [llm, setLlm] = useState<{ available: boolean; model: string | null }>({
    available: false,
    model: null,
  })
  // The brief version the currently-shown run was scored against, so a later
  // chat-driven brief edit can be detected deterministically (see the effect
  // below) instead of relying on the agent remembering to re-run itself.
  const [ranAtVersion, setRanAtVersion] = useState<number | null>(null)

  const brief = useQuery<BriefResponse>({
    queryKey: ['brief', briefId],
    queryFn: () => api.getBrief(briefId!),
    enabled: Boolean(briefId),
  })

  // One planning session per visit, mirroring BriefWorkspace's pattern - a
  // fresh conversation, not shared history with any session already created
  // on the Brief page for the same brief (accepted, pre-existing limitation).
  useEffect(() => {
    if (!briefId || sessionId) return
    api
      .createSession(briefId)
      .then((session) => {
        setSessionId(session.session_id)
        setLlm({ available: session.llm_available, model: session.llm_model })
      })
      .catch(() => setSessionId(null))
  }, [briefId, sessionId])

  const recommend = useMutation({
    mutationFn: () =>
      api.createRun({
        brief_id: briefId!,
        objective,
        radius_m: radius,
        locked_asset_ids: locked,
        excluded_asset_ids: excluded,
        top_n: 200,
      }),
    onSuccess: setRun,
  })

  // Records which brief version this run was launched against, so the
  // deterministic-refresh effect below can tell a later edit apart from the
  // run it already knows about.
  const runRecommendation = useCallback(() => {
    setRanAtVersion(brief.data?.version ?? null)
    recommend.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief.data?.version])

  const savePlan = useMutation({
    mutationFn: () =>
      api.createVersion({
        run_id: run!.run_id,
        name: `${brief.data?.brief.brand ?? 'Campaign'} plan`,
        label: OBJECTIVE_FUNCTION_LABEL[objective],
        locked_asset_ids: locked,
        change_summary: `Optimizer bundle (${OBJECTIVE_FUNCTION_LABEL[objective]})`,
      }),
    onSuccess: (version) => navigate(`/plans/${version.plan_id}`),
  })

  // Run once on arrival so the planner lands on results, not an empty screen.
  useEffect(() => {
    if (briefId && brief.data?.validation?.can_search && !run && !recommend.isPending) {
      runRecommendation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefId, brief.data?.validation?.can_search])

  // Deterministic refresh: if the brief version advances past what the
  // current run was scored against (e.g. the chat agent just edited it),
  // re-run automatically. This never depends on the agent also choosing to
  // call run_recommendation - it's a plain comparison against the shared
  // brief cache. Gating on !recommend.isPending means an edit that lands
  // mid-flight is deferred, not dropped: once the in-flight call resolves,
  // ranAtVersion still holds the earlier value, so this re-fires.
  useEffect(() => {
    const latestVersion = brief.data?.version
    if (
      run &&
      !recommend.isPending &&
      brief.data?.validation?.can_search &&
      typeof latestVersion === 'number' &&
      ranAtVersion !== null &&
      latestVersion > ranAtVersion
    ) {
      runRecommendation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief.data?.version, recommend.isPending])

  // The agent calling run_recommendation directly mid-chat: fetch the full
  // run and update this page's own state in place. Must not navigate -
  // unlike BriefWorkspace's onRunCreated, this page already IS the
  // recommendations screen.
  const handleAgentRun = useCallback(
    (runId: string) => {
      api
        .getRun(runId, 200)
        .then((full) => {
          setRun(full)
          setRanAtVersion(brief.data?.version ?? null)
        })
        .catch(() => {
          /* failure is already surfaced via AgentPanel's own tool_result/error entries */
        })
    },
    [brief.data?.version],
  )

  const selectedIds = useMemo(() => new Set(run?.selected_asset_ids ?? []), [run])

  // Get available media types and asset classes from the run data
  const availableMediaTypes = useMemo(() => {
    if (!run) return []
    const types = new Set(run.ranked.map(s => s.asset.media_type).filter(Boolean))
    return Array.from(types).sort()
  }, [run])

  const availableAssetClasses = useMemo(() => {
    if (!run) return []
    const classes = new Set(run.ranked.map(s => ASSET_CLASS_LABEL[s.asset.asset_class]))
    return Array.from(classes).sort()
  }, [run])

  const visible = useMemo(() => {
    if (!run) return []
    let filtered = onlyBundle
      ? run.ranked.filter((s) => selectedIds.has(s.captura_asset_id))
      : run.ranked

    // Apply media type filter
    if (selectedMediaTypes.length > 0) {
      filtered = filtered.filter((s) =>
        s.asset.media_type && selectedMediaTypes.includes(s.asset.media_type)
      )
    }

    // Apply asset class filter
    if (selectedAssetClasses.length > 0) {
      filtered = filtered.filter((s) =>
        selectedAssetClasses.includes(ASSET_CLASS_LABEL[s.asset.asset_class])
      )
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      filtered = filtered.filter((s) => {
        const name = (s.asset.name || '').toLowerCase()
        const mediaCode = (s.asset.media_code || '').toLowerCase()
        const locality = (s.asset.locality || '').toLowerCase()
        const landmark = (s.asset.landmark || '').toLowerCase()
        const ward = (s.asset.ward || '').toLowerCase()
        const address = (s.asset.address || '').toLowerCase()

        return (
          name.includes(query) ||
          mediaCode.includes(query) ||
          locality.includes(query) ||
          landmark.includes(query) ||
          ward.includes(query) ||
          address.includes(query)
        )
      })
    }

    return filtered
  }, [run, onlyBundle, selectedIds, selectedMediaTypes, selectedAssetClasses, searchQuery])

  const mapAssets: MapAsset[] = useMemo(
    () =>
      visible
        .filter((s) => s.asset.latitude !== null && s.asset.longitude !== null)
        .map((s) => ({
          id: s.captura_asset_id,
          name: s.asset.name,
          latitude: s.asset.latitude!,
          longitude: s.asset.longitude!,
          rank: s.rank,
          selected: selectedIds.has(s.captura_asset_id),
          score: s.total_score,
          mediaType: s.asset.media_type,
          locality: s.asset.locality,
          available: s.availability?.is_available ?? null,
          synthetic: s.contains_synthetic_data,
        })),
    [visible, selectedIds],
  )

  if (!briefId) {
    return (
      <main className="main">
        <div className="page">
          <EmptyState
            title="No brief selected"
            action={
              <button className="primary" onClick={() => navigate('/')}>
                Start a brief
              </button>
            }
          />
        </div>
      </main>
    )
  }

  // Folded into the content area (not a full-page early return) so the
  // chat panel stays mounted and usable even if a chat-driven edit
  // temporarily makes the brief unsearchable.
  const blocked = Boolean(brief.data && !brief.data.validation?.can_search)

  const optimization = run?.optimization
  const metrics = run?.plan_metrics
  const budget = brief.data?.brief.budget?.amount_minor ?? null
  const utilisation = optimization?.budget_utilisation ?? null

  const isRefreshing = recommend.isPending && run !== null

  return (
    <main className="main full">
      <div className="workspace">
        <div className="workspace-main">
          <div>
            <div style={{ padding: '8px 14px 10px' }}>
              <StepIndicator step={2} />
            </div>
            <div className="toolbar">
              <div className="grow">
                <h1 style={{ fontSize: 16 }}>
                  {brief.data?.brief.brand ?? 'Campaign'} recommendations
                </h1>
                <div className="tiny faint">
                  {run
                    ? `${dateRange(run.window.start_date, run.window.end_date)} · ${run.radius_m} m buffer · scoring ${run.scoring_version} · ${run.latency_ms} ms${run.tokens_used > 0 ? ` · ${run.tokens_used.toLocaleString()} tokens` : ''}`
                    : 'Running the pipeline…'}
                </div>
              </div>
              <label className="field" style={{ width: 190 }}>
                Objective
                <select value={objective} onChange={(e) => setObjective(e.target.value)}>
                  {OBJECTIVES.map((value) => (
                    <option key={value} value={value}>
                      {OBJECTIVE_FUNCTION_LABEL[value]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field" style={{ width: 120 }}>
                Buffer (m)
                <select value={radius} onChange={(e) => setRadius(Number(e.target.value))}>
                  {[250, 500, 750, 1000, 2000].map((value) => (
                    <option key={value} value={value}>
                      {value} m
                    </option>
                  ))}
                </select>
              </label>
              <button onClick={() => runRecommendation()} disabled={recommend.isPending}>
                {recommend.isPending ? 'Optimizing…' : 'Re-run'}
              </button>
              <button
                className="primary"
                disabled={!run || !optimization?.feasible || savePlan.isPending || recommend.isPending}
                onClick={() => savePlan.mutate()}
                title={
                  optimization?.feasible
                    ? 'Save this bundle as a plan version'
                    : 'The bundle is not feasible; fix the constraints first'
                }
              >
                {savePlan.isPending ? 'Saving…' : 'Save as plan'}
              </button>
            </div>
          </div>

          {blocked ? (
            <div className="scroll-y" style={{ padding: 14 }}>
              <Callout tone="warn" title="The brief is not complete enough to search">
                Availability and price can only be asserted for a resolved geography, window and
                budget.
                <div style={{ marginTop: 8 }}>
                  <button className="primary small" onClick={() => navigate(`/brief/${briefId}`)}>
                    Complete the brief
                  </button>
                </div>
              </Callout>
            </div>
          ) : (
            <div className="split">
              <div className="map-pane">
                {recommend.isPending && !run ? (
                  <div className="card-body">
                    <Skeleton rows={5} />
                  </div>
                ) : (
                  <AssetMap
                    assets={mapAssets}
                    focusedId={focused}
                    onSelect={(id) => {
                      setFocused(id)
                      setDetail(id)
                    }}
                    bufferRadiusM={run?.radius_m}
                  />
                )}
              </div>

              <div className="scroll-y" style={{ padding: 14 }}>
                <div className="stack">
                  {isRefreshing ? (
                    <Callout tone="info" title="The brief changed — refreshing recommendations…" />
                  ) : null}
                  {recommend.isError ? <ErrorCallout error={recommend.error} /> : null}
                  {savePlan.isError ? <ErrorCallout error={savePlan.error} /> : null}

                  {recommend.isPending && !run ? (
                    <RecommendationProgress />
                  ) : !run ? null : (
                    <>
                      {run.errors.map((error) => (
                        <Callout key={error.code} tone="warn" title={error.message} />
                      ))}

                      {!optimization?.feasible ? (
                        <Callout tone="danger" title="No feasible bundle">
                          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                            {optimization?.infeasibility_reasons.map((reason, i) => (
                              <li key={i}>{reason}</li>
                            ))}
                          </ul>
                          {optimization?.near_feasible_alternatives.length ? (
                            <div style={{ marginTop: 8 }}>
                              <strong>What would help</strong>
                              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                                {optimization.near_feasible_alternatives.map((alt, i) => (
                                  <li key={i}>{String(alt.note)}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                        </Callout>
                      ) : null}

                      {/* -- bundle summary and budget meter -------------------- */}
                      <div className="card">
                        <div className="card-head">
                          <h2 className="grow">Selected bundle</h2>
                          {optimization?.feasible ? (
                            <Chip tone="ok">Feasible</Chip>
                          ) : (
                            <Chip tone="danger">Infeasible</Chip>
                          )}
                        </div>
                        <div className="card-body stack">
                          <div className="grid four">
                            <Metric
                              label="Assets"
                              value={optimization?.selected_asset_ids.length ?? 0}
                              note={
                                metrics
                                  ? `${metrics.locality_count} localities`
                                  : undefined
                              }
                            />
                            <Metric
                              label="Total cost"
                              value={moneyShort(optimization?.total_cost_minor)}
                              note={
                                budget
                                  ? `of ${moneyShort(budget)} budget`
                                  : 'no budget set'
                              }
                            />
                            <Metric
                              label="Budget used"
                              value={percent(utilisation, 1)}
                              tone={
                                utilisation === null
                                  ? undefined
                                  : utilisation > 1
                                    ? 'danger'
                                    : utilisation > 0.9
                                      ? 'ok'
                                      : undefined
                              }
                              note={
                                optimization?.unspent_budget_minor
                                  ? `${moneyShort(optimization.unspent_budget_minor)} unspent`
                                  : undefined
                              }
                            />
                            <Metric
                              label="Mean suitability"
                              value={metrics?.mean_suitability?.toFixed(1) ?? '—'}
                              note={
                                metrics?.mean_confidence
                                  ? `confidence ${percent(metrics.mean_confidence)}`
                                  : undefined
                              }
                            />
                          </div>

                          {budget ? (
                            <div>
                              <div
                                className={`bar ${(utilisation ?? 0) > 1 ? 'over' : (utilisation ?? 0) > 0.9 ? 'ok' : ''
                                  }`}
                              >
                                <span style={{ width: `${Math.min(100, (utilisation ?? 0) * 100)}%` }} />
                              </div>
                              <div className="tiny faint" style={{ marginTop: 3 }}>
                                {money(optimization?.total_cost_minor ?? 0)} of {money(budget)}
                              </div>
                            </div>
                          ) : null}

                          {metrics && (metrics.media_type_breakdown || metrics.asset_class_breakdown) ? (
                            <BundleCompositionBreakdown
                              metrics={metrics}
                              selectedScores={run.ranked.filter((s) => selectedIds.has(s.captura_asset_id))}
                            />
                          ) : null}

                          {metrics ? (
                            <div className="grid three">
                              <FeatureMetric label="Plan TG fit" feature={metrics.tg_fit} />
                              <FeatureMetric label="Daily effective OTS" feature={metrics.daily_eots} />
                              <FeatureMetric
                                label="Campaign target impressions"
                                feature={metrics.target_impressions}
                              />
                              <FeatureMetric label="CPTI" feature={metrics.cpti_minor} />
                            </div>
                          ) : null}

                          {metrics?.reach_method === 'additive_estimate' ? (
                            <Callout tone="warn" title="Exposure figures are additive">
                              Per-asset effective OTS is summed, so an audience passing more than one
                              asset is counted more than once. De-duplicated reach needs
                              origin-destination or device data, which is a later phase.
                            </Callout>
                          ) : metrics?.reach_method === 'unavailable' ? (
                            <Callout tone="info" title="Reach and frequency are unavailable">
                              Compare plans on cost, suitability and coverage instead.
                            </Callout>
                          ) : null}

                          {optimization?.constraint_checks.length ? (
                            <details className="disclosure">
                              <summary>Constraint report</summary>
                              <div className="table-wrap">
                                <table>
                                  <thead>
                                    <tr>
                                      <th>Constraint</th>
                                      <th>Kind</th>
                                      <th>Target</th>
                                      <th>Actual</th>
                                      <th />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {optimization.constraint_checks.map((check) => (
                                      <tr key={check.name}>
                                        <td>{titleCase(check.name)}</td>
                                        <td className="small muted">{check.kind}</td>
                                        <td className="small">{formatConstraint(check.target)}</td>
                                        <td className="small">{formatConstraint(check.actual)}</td>
                                        <td>
                                          <Chip tone={check.satisfied ? 'ok' : check.kind === 'hard' ? 'danger' : 'warn'}>
                                            {check.satisfied ? 'met' : 'not met'}
                                          </Chip>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="tiny faint" style={{ marginTop: 6 }}>
                                {optimization.algorithm} · {optimization.iterations} iterations · seed{' '}
                                <span className="mono">{optimization.deterministic_seed}</span>
                              </div>
                            </details>
                          ) : null}
                        </div>
                      </div>

                      {/* -- eligibility funnel --------------------------------- */}
                      <div className="card">
                        <div className="card-head">
                          <h2 className="grow">Eligibility</h2>
                          <span className="small muted">
                            {run.eligibility.considered} considered → {run.eligibility.eligible} ranked
                          </span>
                        </div>
                        <div className="card-body">
                          {Object.keys(run.eligibility.excluded_by_code).length ? (
                            <div className="stack tight">
                              {Object.entries(run.eligibility.excluded_by_code)
                                .sort((a, b) => b[1] - a[1])
                                .map(([code, count]) => (
                                  <div key={code} className="row between">
                                    <span className="small">
                                      {EXCLUSION_LABEL[code] ?? titleCase(code)}
                                    </span>
                                    <span className="chip neutral tnum">{count}</span>
                                  </div>
                                ))}
                              <details className="disclosure">
                                <summary>Which assets, and why</summary>
                                <div className="table-wrap">
                                  <table>
                                    <thead>
                                      <tr>
                                        <th>Asset</th>
                                        <th>Reason</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {run.eligibility.samples.map((sample) => (
                                        <tr key={`${sample.captura_asset_id}-${sample.code}`}>
                                          <td className="mono tiny">{sample.asset_name}</td>
                                          <td className="small">
                                            {sample.reason}
                                            {sample.detail && Object.keys(sample.detail).length ? (
                                              <div className="tiny faint">
                                                {JSON.stringify(sample.detail).slice(0, 160)}
                                              </div>
                                            ) : null}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </details>
                            </div>
                          ) : (
                            <div className="small muted">
                              Every asset that matched the brief was eligible.
                            </div>
                          )}
                        </div>
                      </div>

                      {/* -- ranked list ---------------------------------------- */}
                      <div className="card">
                        <div className="card-head">
                          <h2 className="grow">Ranked assets</h2>
                          <label className="row tight small" style={{ marginBottom: 0 }}>
                            <input
                              type="checkbox"
                              style={{ width: 'auto' }}
                              checked={onlyBundle}
                              onChange={(e) => setOnlyBundle(e.target.checked)}
                            />
                            Bundle only
                          </label>
                        </div>
                        {/* Filters */}
                        {(availableMediaTypes.length > 0 || availableAssetClasses.length > 0) && (
                          <div className="card-body tight" style={{ borderBottom: '1px solid var(--border)' }}>
                            {/* Search box */}
                            <div style={{ marginBottom: 12 }}>
                              <input
                                type="text"
                                placeholder="Search by name, media code, or location..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '6px 10px',
                                  fontSize: '13px',
                                  border: '1px solid var(--border)',
                                  borderRadius: '4px',
                                  outline: 'none',
                                }}
                                onFocus={(e) => e.target.style.borderColor = 'var(--accent)'}
                                onBlur={(e) => e.target.style.borderColor = 'var(--border)'}
                              />
                            </div>
                            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                              {availableMediaTypes.length > 0 && (
                                <div className="row tight" style={{ gap: 4 }}>
                                  <span className="tiny faint">Media Type:</span>
                                  {availableMediaTypes.map((type) => (
                                    <button
                                      key={type}
                                      className={`chip ${selectedMediaTypes.includes(type as string) ? 'primary' : 'neutral'}`}
                                      onClick={() =>
                                        setSelectedMediaTypes((prev: any) =>
                                          prev.includes(type)
                                            ? prev.filter((t: string) => t !== type)
                                            : [...prev, type]
                                        )
                                      }
                                      style={{ cursor: 'pointer' }}
                                    >
                                      {type}
                                    </button>
                                  ))}
                                  {selectedMediaTypes.length > 0 && (
                                    <button
                                      className="tiny ghost"
                                      onClick={() => setSelectedMediaTypes([])}
                                      style={{ padding: '2px 6px' }}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                              )}
                              {availableAssetClasses.length > 0 && (
                                <div className="row tight" style={{ gap: 4 }}>
                                  <span className="tiny faint">Asset Class:</span>
                                  {availableAssetClasses.map((assetClass) => (
                                    <button
                                      key={assetClass}
                                      className={`chip ${selectedAssetClasses.includes(assetClass) ? 'primary' : 'neutral'}`}
                                      onClick={() =>
                                        setSelectedAssetClasses((prev) =>
                                          prev.includes(assetClass)
                                            ? prev.filter((c) => c !== assetClass)
                                            : [...prev, assetClass]
                                        )
                                      }
                                      style={{ cursor: 'pointer' }}
                                    >
                                      {assetClass}
                                    </button>
                                  ))}
                                  {selectedAssetClasses.length > 0 && (
                                    <button
                                      className="tiny ghost"
                                      onClick={() => setSelectedAssetClasses([])}
                                      style={{ padding: '2px 6px' }}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                            {(selectedMediaTypes.length > 0 || selectedAssetClasses.length > 0 || searchQuery.trim()) && (
                              <div className="tiny faint" style={{ marginTop: 6 }}>
                                Showing {visible.length} of {run?.ranked.length ?? 0} assets
                                {searchQuery.trim() && (
                                  <button
                                    className="tiny ghost"
                                    onClick={() => setSearchQuery('')}
                                    style={{ marginLeft: 8, padding: '2px 6px' }}
                                  >
                                    Clear search
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="table-wrap" style={{ maxHeight: 460 }}>
                          <table>
                            <thead>
                              <tr>
                                <th className="num">#</th>
                                <th>Asset</th>
                                <th className="num">Score</th>
                                <th className="num">Cost</th>
                                <th>Status</th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              {visible.map((score) => (
                                <AssetRow
                                  key={score.captura_asset_id}
                                  score={score}
                                  inBundle={selectedIds.has(score.captura_asset_id)}
                                  focused={focused === score.captura_asset_id}
                                  locked={locked.includes(score.captura_asset_id)}
                                  excluded={excluded.includes(score.captura_asset_id)}
                                  onFocus={() => setFocused(score.captura_asset_id)}
                                  onDetail={() => setDetail(score.captura_asset_id)}
                                  onToggleLock={() =>
                                    setLocked((prev) =>
                                      prev.includes(score.captura_asset_id)
                                        ? prev.filter((id) => id !== score.captura_asset_id)
                                        : [...prev, score.captura_asset_id],
                                    )
                                  }
                                  onToggleExclude={() =>
                                    setExcluded((prev) =>
                                      prev.includes(score.captura_asset_id)
                                        ? prev.filter((id) => id !== score.captura_asset_id)
                                        : [...prev, score.captura_asset_id],
                                    )
                                  }
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {locked.length || excluded.length ? (
                          <div className="card-body tight row between">
                            <span className="small muted">
                              {locked.length} locked · {excluded.length} excluded
                            </span>
                            <button
                              className="small primary"
                              disabled={recommend.isPending}
                              onClick={() => runRecommendation()}
                            >
                              Re-optimize with these
                            </button>
                          </div>
                        ) : null}
                      </div>

                      {/* -- assumptions ---------------------------------------- */}
                      <div className="card">
                        <div className="card-head">
                          <h2 className="grow">Assumptions</h2>
                          <span className="tiny faint">An approver must acknowledge these</span>
                        </div>
                        <div className="card-body stack tight">
                          {run.assumptions.map((assumption) => (
                            <Callout
                              key={assumption.key}
                              tone={
                                assumption.severity === 'critical'
                                  ? 'danger'
                                  : assumption.severity === 'caution'
                                    ? 'warn'
                                    : 'info'
                              }
                              title={assumption.statement}
                            >
                              {assumption.impact}
                            </Callout>
                          ))}
                          {run.scoring_note ? (
                            <div className="tiny faint">{run.scoring_note}</div>
                          ) : null}
                        </div>
                      </div>

                      {/* -- data coverage -------------------------------------- */}
                      <details className="card">
                        <summary className="card-head" style={{ cursor: 'pointer' }}>
                          <h2 className="grow">Data coverage across {run.coverage.assets} assets</h2>
                        </summary>
                        <div className="table-wrap" style={{ maxHeight: 340 }}>
                          <table>
                            <thead>
                              <tr>
                                <th>Feature</th>
                                <th className="num">Coverage</th>
                                <th>Classes</th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(run.coverage.features)
                                .sort((a, b) => a[1].coverage - b[1].coverage)
                                .map(([key, stats]) => (
                                  <tr key={key}>
                                    <td className="small">{stats.label}</td>
                                    <td className="num">
                                      <div className="row tight" style={{ justifyContent: 'flex-end' }}>
                                        <span className="tnum">{percent(stats.coverage)}</span>
                                        <div className="bar" style={{ width: 54 }}>
                                          <span style={{ width: `${stats.coverage * 100}%` }} />
                                        </div>
                                      </div>
                                    </td>
                                    <td className="pill-row">
                                      {Object.keys(stats.data_classes).map((cls) => (
                                        <DataClassChip key={cls} value={cls as any} />
                                      ))}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </details>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <ResizablePanel storageKey="agentPanel" title="Planning assistant">
          <AgentPanel
            sessionId={sessionId}
            llmAvailable={llm.available}
            llmModel={llm.model}
            onStateChange={(data) => applyBriefStateEvent(queryClient, briefId!, data)}
            onRunCreated={handleAgentRun}
          />
        </ResizablePanel>
      </div>

      {detail && run ? (
        <AssetDetail
          score={run.ranked.find((s) => s.captura_asset_id === detail) ?? null}
          briefId={briefId}
          radius={run.radius_m}
          alternatives={optimization?.alternatives?.[detail] ?? []}
          onClose={() => setDetail(null)}
        />
      ) : null}
    </main>
  )
}

function AssetRow({
  score,
  inBundle,
  focused,
  locked,
  excluded,
  onFocus,
  onDetail,
  onToggleLock,
  onToggleExclude,
}: {
  score: AssetScoreOut
  inBundle: boolean
  focused: boolean
  locked: boolean
  excluded: boolean
  onFocus: () => void
  onDetail: () => void
  onToggleLock: () => void
  onToggleExclude: () => void
}) {
  return (
    <tr
      className={inBundle ? 'selected' : undefined}
      onMouseEnter={onFocus}
      onFocus={onFocus}
      style={focused ? { outline: '2px solid var(--accent)', outlineOffset: -2 } : undefined}
    >
      <td className="num tnum">{score.rank}</td>
      <td>
        <button
          className="ghost small"
          style={{ padding: 0, textAlign: 'left', whiteSpace: 'normal' }}
          onClick={onDetail}
        >
          <span style={{ fontWeight: 550 }}>{score.asset.name}</span>
        </button>
        <div className="tiny faint">
          {score.asset.media_type} · {ASSET_CLASS_LABEL[score.asset.asset_class]}
          {score.asset.locality ? ` · ${score.asset.locality}` : ''}
          {score.asset.strategy_tier ? ` · ${score.asset.strategy_tier}` : ''}
        </div>
      </td>
      <td className="num tnum" style={{ fontWeight: 600 }}>
        {score.total_score.toFixed(1)}
        <div>
          <ConfidenceChip value={score.confidence} />
        </div>
      </td>
      <td className="num tnum nowrap">
        {score.price?.total_cost_minor ? moneyShort(score.price.total_cost_minor) : '—'}
      </td>
      <td>
        <div className="pill-row">
          <AvailabilityChip
            available={score.availability?.is_available}
            bookedDays={score.availability?.booked_dates.length}
          />
          {inBundle ? <Chip tone="ok">in bundle</Chip> : null}
        </div>
      </td>
      <td>
        <div className="row tight">
          <button
            className={`small ${locked ? 'primary' : 'ghost'}`}
            onClick={onToggleLock}
            title="Keep this asset in every re-optimization"
          >
            {locked ? 'Locked' : 'Lock'}
          </button>
          <button
            className={`small ${excluded ? 'danger' : 'ghost'}`}
            onClick={onToggleExclude}
            title="Never select this asset"
          >
            {excluded ? 'Excluded' : 'Exclude'}
          </button>
        </div>
      </td>
    </tr>
  )
}

function BundleCompositionBreakdown({
  metrics,
  selectedScores,
}: {
  metrics: RunResponse['plan_metrics']
  selectedScores: AssetScoreOut[]
}) {
  if (!metrics) return null

  // Calculate cost breakdown by media type
  const costByMediaType: Record<string, number> = {}
  const costByAssetClass: Record<string, number> = {}

  selectedScores.forEach((score) => {
    const cost = score.price?.total_cost_minor || 0
    const mediaType = score.asset.media_type || 'Unknown'
    const assetClass = ASSET_CLASS_LABEL[score.asset.asset_class] || 'Unknown'

    costByMediaType[mediaType] = (costByMediaType[mediaType] || 0) + cost
    costByAssetClass[assetClass] = (costByAssetClass[assetClass] || 0) + cost
  })

  const totalCost = metrics.total_cost_minor || 1
  const totalAssets = metrics.asset_count || 1

  const mediaTypeEntries = Object.entries(metrics.media_type_breakdown || {})
    .sort((a, b) => b[1] - a[1])
  const assetClassEntries = Object.entries(metrics.asset_class_breakdown || {})
    .sort((a, b) => b[1] - a[1])

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
      <div className="small" style={{ fontWeight: 600, marginBottom: 8 }}>
        Bundle Composition
      </div>

      {mediaTypeEntries.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div className="tiny faint" style={{ marginBottom: 6 }}>By Media Type</div>
          <div className="stack tight">
            {mediaTypeEntries.map(([mediaType, count]) => {
              const cost = costByMediaType[mediaType] || 0
              const assetPct = (count / totalAssets) * 100
              const costPct = totalCost > 0 ? (cost / totalCost) * 100 : 0

              return (
                <div key={mediaType} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <div className="small" style={{ fontWeight: 500, minWidth: 0, flex: '1 1 auto' }}>{mediaType}</div>
                    <div className="tiny faint" style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}>
                      {count} assets ({assetPct.toFixed(1)}%)
                    </div>
                  </div>
                  <div className="tiny faint" style={{ marginBottom: 2 }}>
                    {moneyShort(cost)} ({costPct.toFixed(1)}% of cost)
                  </div>
                  <div className="bar" style={{ height: 6, background: 'var(--gray-200)', width: '100%' }}>
                    <span
                      style={{
                        width: `${Math.min(100, costPct)}%`,
                        background: 'var(--accent)'
                      }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {assetClassEntries.length > 0 && (
        <div>
          <div className="tiny faint" style={{ marginBottom: 6 }}>By Asset Class</div>
          <div className="stack tight">
            {assetClassEntries.map(([assetClass, count]) => {
              const displayClass = ASSET_CLASS_LABEL[assetClass] || assetClass
              const cost = costByAssetClass[displayClass] || 0
              const assetPct = (count / totalAssets) * 100
              const costPct = totalCost > 0 ? (cost / totalCost) * 100 : 0

              return (
                <div key={assetClass} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <div className="small" style={{ fontWeight: 500, minWidth: 0, flex: '1 1 auto' }}>{displayClass}</div>
                    <div className="tiny faint" style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}>
                      {count} assets ({assetPct.toFixed(1)}%)
                    </div>
                  </div>
                  <div className="tiny faint" style={{ marginBottom: 2 }}>
                    {moneyShort(cost)} ({costPct.toFixed(1)}% of cost)
                  </div>
                  <div className="bar" style={{ height: 6, background: 'var(--gray-200)', width: '100%' }}>
                    <span
                      style={{
                        width: `${Math.min(100, costPct)}%`,
                        background: 'var(--accent-secondary, var(--accent))'
                      }}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function formatConstraint(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (Array.isArray(value)) return value.length ? `${value.length} item(s)` : 'none'
  if (typeof value === 'number') return value > 10_000 ? moneyShort(value) : String(value)
  return String(value)
}

export { ScoreBreakdown }
